import { Avatar, AvatarGroup, Box, Chip, useTheme } from '@mui/material';
import * as React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { REPEAT_OVERFLOW_COUNTER_AVATAR_DIM } from 'v2/constants';

type SizeOpt = 'small' | 'medium';
type Variant = 'avatar' | 'chip';

type CellWithHiddenProps = {
  children: React.ReactNode;
  gapPx?: number;
  size?: SizeOpt;
  renderMore?: (count: number, opts: { size?: SizeOpt; hiddenItems: React.ReactNode[] }) => React.ReactNode;

  // rerenderKey – manual key to force re-measuring child widths and the "+n" badge.
  // Change it when rendering/styling may affect widths (renderMore, variant, theme, fonts, etc.).
  // Updating it triggers the useLayoutEffect to recompute itemWidths/moreWidth.
  rerenderKey?: string | number;
  stopPropagation?: boolean;
  variant: Variant;
};

function arraysEqual(a: number[], b: number[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

const RepeatOverflowCounter = ({
  children,
  gapPx = 4,
  size = 'medium',
  renderMore,
  rerenderKey,
  stopPropagation = false,
  variant
}: CellWithHiddenProps) => {
  const theme = useTheme();
  const items = useMemo(() => React.Children.toArray(children), [children]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState<number>(0);

  // State for variant "chip"
  const [itemWidths, setItemWidths] = useState<number[]>([]);
  const [moreWidth, setMoreWidth] = useState<number>(0);

  // state for variant "avatar" (AvatarGroup)
  const [groupWidths, setGroupWidths] = useState<number[]>([]); // width dla max = 1..N

  const firstElement = useMemo(
    () => items.find((n): n is React.ReactElement => React.isValidElement(n)) ?? null,
    [items]
  );

  const autoRenderMore = React.useCallback(
    (count: number) => {
      if (variant === 'avatar') {
        const dim = REPEAT_OVERFLOW_COUNTER_AVATAR_DIM[size];
        const fontSize = size === 'small' ? theme.typography.caption.fontSize : theme.typography.body2.fontSize;
        return <Avatar sx={{ width: dim, height: dim, fontSize }}>{`+${count}`}</Avatar>;
      }
      return <Chip size={size} label={`+${count}`} variant="outlined" />;
    },
    [variant, size, theme]
  );

  const moreSampleCount = useMemo(() => {
    if (variant === 'avatar') return 0; // nieużywane dla avatar
    const digits = Math.max(1, String(items.length).length);
    return Number('9'.repeat(digits));
  }, [variant, items.length]);

  const stop = React.useCallback(
    (e: React.SyntheticEvent) => {
      if (stopPropagation) e.stopPropagation();
    },
    [stopPropagation]
  );

  // style and spacing for variant "avatar"
  const avatarDim = REPEAT_OVERFLOW_COUNTER_AVATAR_DIM[size];
  const avatarFontSize = size === 'small' ? theme.typography.caption.fontSize : theme.typography.body2.fontSize;
  const avatarItemSx = {
    width: avatarDim,
    height: avatarDim,
    fontSize: avatarFontSize
  } as const;
  const spacingValue = typeof gapPx === 'number' ? Math.abs(gapPx) : size === 'small' ? 'small' : 'medium';

  useLayoutEffect(() => {
    const measure = () => {
      const root = measureRef.current;
      if (!root) {
        if (variant === 'avatar') {
          setGroupWidths([]);
        } else {
          setItemWidths([]);
          setMoreWidth(0);
        }
        return;
      }
      if (variant === 'avatar') {
        const nodes = Array.from(root.querySelectorAll('[data-measure-group="true"]')) as HTMLElement[];
        const widths = nodes.slice(0, items.length).map((el) => Math.ceil(el.getBoundingClientRect().width));
        setGroupWidths((prev) => (arraysEqual(prev, widths) ? prev : widths));
      } else {
        const nodes = Array.from(root.querySelectorAll('[data-measure-item="true"]')) as HTMLElement[];
        if (nodes.length === 0) {
          setItemWidths([]);
          setMoreWidth(0);
          return;
        }
        const widths = nodes.slice(0, items.length).map((el) => Math.ceil(el.getBoundingClientRect().width));
        const moreNode = root.querySelector('[data-measure-more="true"]') as HTMLElement | null;
        const nextMoreWidth = Math.ceil(moreNode?.getBoundingClientRect().width || 0);

        setItemWidths((prev) => (arraysEqual(prev, widths) ? prev : widths));
        setMoreWidth((prev) => (prev === nextMoreWidth ? prev : nextMoreWidth));
      }
    };

    const raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [items, size, gapPx, variant, firstElement?.type, moreSampleCount, rerenderKey]);

  const hasRenderMore = !!renderMore;

  const recompute = React.useCallback(() => {
    const c = containerRef.current;
    if (!c) {
      setVisibleCount((prev) => (prev === items.length ? prev : items.length));
      return;
    }

    const available = c.clientWidth;

    if (variant === 'avatar') {
      if (groupWidths.length === 0) {
        setVisibleCount((prev) => (prev === items.length ? prev : items.length));
        return;
      }

      let nextVisible = 0;
      for (let k = 1; k <= items.length; k++) {
        // When renderMore is provided and items will overflow, we render k real avatars in
        // AvatarGroup + an external avatar with -gapPx margin (same overlap as internal).
        // That total width equals groupWidths[k] (k+1 internal-style elements), not groupWidths[k-1].
        const willOverflow = hasRenderMore && k < items.length;
        const w = groupWidths[willOverflow ? k : k - 1];
        if (w === undefined || w > available) break;
        nextVisible = k;
      }

      if (nextVisible === 0 && items.length > 0) {
        nextVisible = 1;
      }

      setVisibleCount((prev) => (prev === nextVisible ? prev : nextVisible));
      return;
    }

    if (itemWidths.length === 0) {
      setVisibleCount((prev) => (prev === items.length ? prev : items.length));
      return;
    }

    let used = 0;
    let idx = 0;

    for (; idx < itemWidths.length; idx++) {
      const remainingAfterThis = itemWidths.length - (idx + 1);
      const reserveForMore = remainingAfterThis > 0 ? (idx > 0 ? gapPx : 0) + moreWidth : 0;
      const widthWithGap = itemWidths[idx] + (idx > 0 ? gapPx : 0);

      if (used + widthWithGap + reserveForMore <= available) {
        used += widthWithGap;
      } else {
        break;
      }
    }

    const nextVisible = idx === 0 && moreWidth <= available ? 0 : idx;
    setVisibleCount((prev) => (prev === nextVisible ? prev : nextVisible));
  }, [variant, groupWidths, itemWidths, moreWidth, gapPx, items.length, hasRenderMore]);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    let rafId = 0;
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        recompute();
        rafId = 0;
      });
    });

    ro.observe(c);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [recompute]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  const hiddenCount = Math.max(0, items.length - visibleCount);
  const hiddenItems = items.slice(visibleCount);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'inline-flex',
          verticalAlign: 'middle',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box'
        }}
      >
        {variant === 'avatar' ? (
          <span
            onMouseDown={stop}
            onClick={stop}
            style={{ display: 'inline-flex', width: '100%', alignItems: 'center' }}
          >
            <AvatarGroup
              max={renderMore ? visibleCount + 1 : visibleCount}
              spacing={spacingValue as any}
              sx={{ zIndex: 1, '& .MuiAvatar-root': avatarItemSx }}
              slotProps={{
                additionalAvatar: { sx: avatarItemSx, variant: 'circular' }
              }}
            >
              {(renderMore ? items.slice(0, visibleCount) : items) as any}
            </AvatarGroup>
            {renderMore && hiddenCount > 0 && (
              <span style={{ display: 'inline-flex', marginLeft: -gapPx }} onMouseDown={stop} onClick={stop}>
                {renderMore(hiddenCount, { size, hiddenItems })}
              </span>
            )}
          </span>
        ) : (
          <>
            {items.slice(0, visibleCount).map((node, idx) => (
              <span
                key={`v-${(node as React.ReactElement).key ?? idx}`}
                style={{
                  display: 'inline-flex',
                  marginRight: idx < visibleCount - 1 ? gapPx : 0
                }}
                onMouseDown={stop}
                onClick={stop}
              >
                {node}
              </span>
            ))}

            {hiddenCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  marginLeft: visibleCount > 0 ? gapPx : 0
                }}
                onMouseDown={stop}
                onClick={stop}
              >
                {renderMore ? renderMore(hiddenCount, { size, hiddenItems }) : autoRenderMore(hiddenCount)}
              </span>
            )}
          </>
        )}
      </Box>

      {/* hidden container for calculation */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}
      >
        {variant === 'avatar' ? (
          items.length > 0 ? (
            Array.from({ length: items.length }, (_, idx) => {
              const k = idx + 1;
              return (
                <div key={`mg-${k}`} data-measure-group="true" style={{ display: 'inline-flex' }}>
                  <AvatarGroup
                    max={k}
                    spacing={spacingValue as any}
                    sx={{ '& .MuiAvatar-root': avatarItemSx }}
                    slotProps={{
                      additionalAvatar: {
                        sx: avatarItemSx,
                        variant: 'circular'
                      }
                    }}
                  >
                    {items as any}
                  </AvatarGroup>
                </div>
              );
            })
          ) : null
        ) : (
          <>
            {items.map((node, idx) => (
              <span key={`m-${idx}`} data-measure-item="true" style={{ display: 'inline-flex' }}>
                {node}
              </span>
            ))}
            <span data-measure-more="true" style={{ display: 'inline-flex' }}>
              {renderMore
                ? renderMore(Math.max(1, moreSampleCount), { size, hiddenItems: [] })
                : autoRenderMore(Math.max(1, moreSampleCount))}
            </span>
          </>
        )}
      </Box>
    </>
  );
};

export default RepeatOverflowCounter;
